import { Injectable, Inject } from '@angular/core'
import { UserManager, User, SignoutResponse } from 'oidc-client-ts'
import { Observable, from } from 'rxjs'
import { map, catchError } from 'rxjs/operators'
import { OIDCClientConfig, OIDC_CLIENT_CONFIG } from './oidc-client-config'
import { IUser } from './user'
import { AuthModel } from './store/auth-model'
import { Store } from '@ngrx/store'
import { user_loaded, user_unloaded, user_signed_out } from './store/actions/auth-action-types'
import { Router } from '@angular/router'
import { SessionTimeoutService } from './session-timeout.service'
import * as Oidc from 'oidc-client-ts'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    @Inject(OIDC_CLIENT_CONFIG) private config: OIDCClientConfig,
    private store: Store<AuthModel>,
    private router: Router,
    private sessionTimeoutService: SessionTimeoutService
  ) {
    if (config.outputOIDCDebugLogs) {
      Oidc.Log.setLogger(console)
      Oidc.Log.setLevel(Oidc.Log.DEBUG)
    }

    this.manager = new UserManager(this.config.userManagerSettings)

    this.manager.events.addUserLoaded(user => {
      window.sessionStorage.setItem('access_token', user.access_token)
      this.store.dispatch(
        user_loaded({
          user: this.mapOIDCUserToAppUser(user)
        })
      )
    })

    this.manager.events.addUserUnloaded(() => {
      this.store.dispatch(user_unloaded())
    })

    this.manager.events.addUserSignedOut(() => {
      this.store.dispatch(user_signed_out())
      this.startAuthentication(this.router.url)
    })

    this.manager.events.addSilentRenewError(() => {
      this.manager.stopSilentRenew()
      this.manager.startSilentRenew()
    })

    this.sessionTimeoutService.startIdleMonitor()
  }

  private manager: UserManager

  public getAuthorizationHeaderValue(): Observable<string> {
    return from(this.manager.getUser()).pipe(
      map((u: User) => (u !== null ? `${u.token_type} ${u.access_token}` : '')),
      catchError((_, caught) => caught)
    )
  }

  public startAuthentication(destination = '/'): Observable<any> {
    return from(
      this.manager.signinRedirect({
        state: destination
      })
    )
  }

  public startLogout(destination = '/'): Observable<void> {
    return from(
      this.manager.signoutRedirect({
        state: destination
      })
    ).pipe(catchError((_, caught) => caught))
  }

  public completeLogout(): Observable<SignoutResponse> {
    return from(this.manager.signoutRedirectCallback()).pipe(catchError((_, caught) => caught))
  }

  public completeAuthentication(): Observable<User> {
    return from(this.manager.signinRedirectCallback().then()).pipe(
      catchError((_, caught) => caught)
    )
  }

  public completeSilentAuthentication(): Observable<void | User> {
    return from(this.manager.signinSilentCallback()).pipe(catchError((_, caught) => caught))
  }

  public getAppUserInfo(): Observable<IUser> {
    return from(this.manager.getUser()).pipe(map(this.mapOIDCUserToAppUser))
  }

  private mapOIDCUserToAppUser(u: User): IUser {
    return u && !u.expired && u.profile
      ? ({
          FirstName: u.profile.given_name,
          LastName: u.profile.family_name,
          Id: u.profile.sub,
          Roles: Array.isArray(u.profile.roles) ? u.profile.roles : [u.profile.roles],
          Permissions: u.profile.permissions
            ? [].concat(u.profile.permissions).map(p => parseInt(p, 10))
            : [],
          Features: u.profile.features
            ? [].concat(u.profile.features).map(p => parseInt(p, 10))
            : [],
          DefaultNavigation: u.profile.default_navigation
        } as IUser)
      : (null as IUser)
  }
}
